import React, { useState, useEffect, useRef } from "react";
import { Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    Decimation
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import crosshairPlugin from "chartjs-plugin-crosshair";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    Decimation,
    zoomPlugin,
    crosshairPlugin
);

const RVIVSeparateTimeSeriesChart = () => {
    const [data, setData] = useState([]);
    const [timestamp, setTimestamp] = useState("");
    const [loading, setLoading] = useState(true);
    const chartRef = useRef(null);

    const API_URL = process.env.REACT_APP_API_URL || "";

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(`${API_URL}/data/volatility`);
                if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
                const { data: jsonData, timestamp: fileTimestamp } = await response.json();
                setData(jsonData);
                setTimestamp(fileTimestamp);
            } catch (error) {
                console.error("Error fetching volatility data:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [API_URL]);

    if (loading) return <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>;
    if (data.length === 0) return <div style={{ textAlign: 'center', padding: '20px' }}>No data available</div>;

    const twoYearsAgo = new Date();
    twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
    const twoYearsAgoStr = twoYearsAgo.toISOString().split('T')[0];
    const startYear2022Idx = data.findIndex(d => d.date >= twoYearsAgoStr);
    const initialMinIdx = startYear2022Idx >= 0 ? startYear2022Idx : 0;

    const labels = data.map(d => d.date);
    const closeData = data.map((d, i) => ({ x: i, y: d.close }));
    // RV and IV are already in % (e.g. 0.79 = 0.79% daily move)
    const rvData = data.map((d, i) => ({ x: i, y: d.realized_vol != null ? d.realized_vol : null }));
    const ivData = data.map((d, i) => ({ x: i, y: d.implied_vol != null ? d.implied_vol : null }));

    const chartData = {
        labels,
        datasets: [
            {
                label: "S&P 500 Close",
                data: closeData,
                borderColor: 'green',
                backgroundColor: 'green',
                borderWidth: 1.5,
                tension: 0,
                fill: false,
                yAxisID: "y",
                pointRadius: 0,
                pointHitRadius: 0,
                order: 3,
            },
            {
                label: "Realized Vol (RV)",
                data: rvData,
                borderColor: 'rgba(70, 130, 210, 0.9)',
                backgroundColor: 'rgba(70, 130, 210, 0.9)',
                borderWidth: 1.5,
                tension: 0,
                fill: false,
                yAxisID: "y1",
                pointRadius: 0,
                pointHitRadius: 0,
                order: 1,
                spanGaps: false,
            },
            {
                label: "Implied Vol (IV)",
                data: ivData,
                borderColor: 'rgba(220, 80, 60, 0.9)',
                backgroundColor: 'rgba(220, 80, 60, 0.9)',
                borderWidth: 1.5,
                tension: 0,
                fill: false,
                yAxisID: "y1",
                pointRadius: 0,
                pointHitRadius: 0,
                order: 2,
                spanGaps: false,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        parsing: false,
        normalized: true,
        plugins: {
            decimation: { enabled: true, algorithm: 'lttb', samples: 500 },
            legend: {
                position: "top",
                labels: {
                    font: { size: window.innerWidth < 600 ? 10 : 12 },
                    usePointStyle: true,
                },
            },
            title: { display: false },
            tooltip: {
                mode: "index",
                intersect: false,
                position: "nearest",
                callbacks: {
                    title: (items) => {
                        if (items.length > 0) {
                            const idx = Math.round(items[0].raw?.x ?? items[0].dataIndex);
                            return idx >= 0 && idx < labels.length ? labels[idx] : '';
                        }
                        return '';
                    },
                    label: (context) => {
                        const value = context.raw?.y ?? context.raw;
                        if (value === null || value === undefined) return null;
                        if (context.dataset.label === "S&P 500 Close") return `Close: $${value.toFixed(2)}`;
                        if (context.dataset.label === "Realized Vol (RV)") return `RV: ${value.toFixed(2)}% daily`;
                        if (context.dataset.label === "Implied Vol (IV)") return `IV: ${value.toFixed(2)}% daily`;
                        return `${context.dataset.label}: ${value}`;
                    },
                },
            },
            zoom: {
                pan: { enabled: true, mode: "x" },
                zoom: {
                    wheel: { enabled: true, speed: 0.09 },
                    pinch: { enabled: true, speed: 0.09 },
                    mode: "x",
                },
            },
            crosshair: {
                line: { color: "rgba(0, 0, 0, 0.2)", width: 1 },
                sync: { enabled: false },
                zoom: { enabled: false },
            },
        },
        interaction: { mode: "nearest", intersect: false, axis: "x" },
        scales: {
            x: {
                type: 'linear',
                min: initialMinIdx,
                max: data.length - 1,
                title: { display: true, text: "Date" },
                ticks: {
                    autoSkip: true,
                    maxTicksLimit: 10,
                    font: { size: 11 },
                    callback: (value) => {
                        const idx = Math.round(value);
                        return idx >= 0 && idx < labels.length ? labels[idx] : '';
                    },
                },
            },
            y: {
                type: "linear",
                position: "left",
                title: { display: true, text: "S&P 500 Close ($)" },
                grid: { drawOnChartArea: true, color: "rgba(200, 200, 200, 0.2)" },
            },
            y1: {
                type: "linear",
                position: "right",
                title: { display: true, text: "Daily Vol (% move)" },
                grid: { drawOnChartArea: false },
                ticks: { callback: (v) => `${v.toFixed(2)}%` },
            },
        },
    };

    return (
        <div style={{ width: '100%' }}>
            <div style={{ height: '370px' }}>
                <Line ref={chartRef} data={chartData} options={options} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', padding: '8px 0' }}>
                <span style={{ fontSize: '12px', color: '#666' }}>Updated: {timestamp}</span>
                <button
                    onClick={() => chartRef.current?.resetZoom()}
                    style={{
                        padding: '6px 12px',
                        fontSize: '12px',
                        backgroundColor: '#4a90d9',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                    }}
                >
                    Reset Zoom
                </button>
            </div>
        </div>
    );
};

export default RVIVSeparateTimeSeriesChart;
